import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useOnboarding, useForwardAction } from '@/components/onboarding'
import {
  PROTOCOL_PLACEHOLDER_NOTE,
  PROTOCOL_SECTIONS,
} from '@/data/onboarding-protocol'
import { protocolReviewStepDataSchema } from '@/schemas/onboarding-protocol'
import type { ProtocolReviewStepData } from '@/types/onboarding'

const STEP_INDEX = 8

function getStoredData(
  stepData: Record<string, unknown>,
): ProtocolReviewStepData | null {
  const stored = stepData[STEP_INDEX]
  if (!stored) return null
  const parsed = protocolReviewStepDataSchema.safeParse(stored)
  return parsed.success ? parsed.data : null
}

export function ProtocolReviewStep() {
  const { state, setStepData, markCompleted, goToStep } = useOnboarding()

  const [acknowledged, setAcknowledged] = useState<boolean>(() => {
    const stored = getStoredData(state.stepData)
    return stored?.acknowledged === true
  })

  const onSubmit = () => {
    const payload: ProtocolReviewStepData = {
      acknowledged: true,
      acknowledged_at: new Date().toISOString(),
    }
    setStepData(STEP_INDEX, payload)
    markCompleted(STEP_INDEX)
    goToStep(STEP_INDEX + 1)
  }

  useForwardAction({
    label: 'Continue',
    disabled: !acknowledged,
    onClick: onSubmit,
  })

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Protocol review</h2>
        <p className="mt-1 text-sm text-slate-600">
          Please review the clinical scope, AI workflow, and platform conventions
          before going live.
        </p>
        <p className="mt-2 text-xs italic text-slate-500">
          {PROTOCOL_PLACEHOLDER_NOTE}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Protocol</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ScrollArea className="h-80 rounded border p-4">
            <div className="space-y-6">
              {PROTOCOL_SECTIONS.map((section) => (
                <section key={section.id} className="space-y-2">
                  <h3 className="text-sm font-semibold text-slate-900">
                    {section.title}
                  </h3>
                  <p className="whitespace-pre-wrap text-sm text-slate-600">
                    {section.body}
                  </p>
                </section>
              ))}
            </div>
          </ScrollArea>

          <div className="flex items-center gap-2">
            <Checkbox
              id="protocol-acknowledge"
              checked={acknowledged}
              onCheckedChange={(val) => setAcknowledged(val === true)}
            />
            <Label htmlFor="protocol-acknowledge" className="text-sm leading-none">
              I have read and acknowledge these protocols.
            </Label>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
